import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDebounce } from './useDebounce';

function parseMultiParam(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) {
    return [];
  }
  return raw.split(',').filter(Boolean);
}

function setMultiParam(params: URLSearchParams, key: string, values: string[]): void {
  if (values.length === 0) {
    params.delete(key);
  } else {
    params.set(key, values.join(','));
  }
}

export interface SubmissionFilters {
  searchInput: string;
  debouncedSearch: string;
  roles: string[];
  sites: string[];
  stages: string[];
  jobPostingId: string;
  dateFrom: string;
  dateTo: string;
  page: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  activeFilterCount: number;
  setSearchInput: (value: string) => void;
  setRoles: (values: string[]) => void;
  setSites: (values: string[]) => void;
  setStages: (values: string[]) => void;
  setJobPostingId: (value: string) => void;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  setPage: (page: number) => void;
  setSortBy: (field: string) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  handleSort: (field: string) => void;
  clearAll: () => void;
  viewMode: 'table' | 'kanban';
  setViewMode: (mode: 'table' | 'kanban') => void;
}

export function useSubmissionFilters(): SubmissionFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebounce(searchInput, 300);
  const lastSyncedSearch = useRef(debouncedSearch);

  // Sync debounced search value to URL and reset page — but only when the
  // search term actually changed. `setSearchParams` from react-router changes
  // identity on every URL update, so without this guard the effect re-fires on
  // any param change (e.g. paging) and would wipe the `page` param each time.
  useEffect(() => {
    if (lastSyncedSearch.current === debouncedSearch) {
      return;
    }
    lastSyncedSearch.current = debouncedSearch;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set('q', debouncedSearch);
        } else {
          next.delete('q');
        }
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setSearchParams]);

  // Sync searchInput from URL when q param changes externally (browser Back/Forward)
  useEffect(() => {
    const currentQ = searchParams.get('q') ?? '';
    if (currentQ !== debouncedSearch) {
      setSearchInput(currentQ);
    }
    // Only react to URL changes, not debouncedSearch changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const roles = useMemo(() => parseMultiParam(searchParams, 'roles'), [searchParams]);
  const sites = useMemo(() => parseMultiParam(searchParams, 'sites'), [searchParams]);
  const stages = useMemo(() => parseMultiParam(searchParams, 'stages'), [searchParams]);
  const jobPostingId = searchParams.get('jobPostingId') ?? '';
  const dateFrom = searchParams.get('from') ?? '';
  const dateTo = searchParams.get('to') ?? '';
  const page = Number(searchParams.get('page') ?? '1');
  const sortBy = searchParams.get('sortBy') ?? '';
  const sortOrder = (searchParams.get('sortOrder') ?? 'desc') as 'asc' | 'desc';
  const viewMode = (searchParams.get('view') ?? 'table') as 'table' | 'kanban';

  const updateParamAndResetPage = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          updater(next);
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setRoles = useCallback(
    (values: string[]) => updateParamAndResetPage((p) => setMultiParam(p, 'roles', values)),
    [updateParamAndResetPage],
  );

  const setSites = useCallback(
    (values: string[]) => updateParamAndResetPage((p) => setMultiParam(p, 'sites', values)),
    [updateParamAndResetPage],
  );

  const setStages = useCallback(
    (values: string[]) => updateParamAndResetPage((p) => setMultiParam(p, 'stages', values)),
    [updateParamAndResetPage],
  );

  const setJobPostingId = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('jobPostingId', value);
        } else {
          p.delete('jobPostingId');
        }
      }),
    [updateParamAndResetPage],
  );

  const setDateFrom = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('from', value);
        } else {
          p.delete('from');
        }
      }),
    [updateParamAndResetPage],
  );

  const setDateTo = useCallback(
    (value: string) =>
      updateParamAndResetPage((p) => {
        if (value) {
          p.set('to', value);
        } else {
          p.delete('to');
        }
      }),
    [updateParamAndResetPage],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newPage <= 1) {
            next.delete('page');
          } else {
            next.set('page', String(newPage));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSortBy = useCallback(
    (field: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (field) {
            next.set('sortBy', field);
          } else {
            next.delete('sortBy');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSortOrder = useCallback(
    (order: 'asc' | 'desc') => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('sortOrder', order);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSort = useCallback(
    (field: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const currentSortBy = next.get('sortBy') ?? '';
          const currentSortOrder = next.get('sortOrder') ?? 'desc';
          if (currentSortBy === field) {
            next.set('sortOrder', currentSortOrder === 'asc' ? 'desc' : 'asc');
          } else {
            next.set('sortBy', field);
            next.set('sortOrder', 'asc');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setViewMode = useCallback(
    (mode: 'table' | 'kanban') => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (mode === 'table') {
            next.delete('view');
          } else {
            next.set('view', mode);
          }
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchInput('');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (debouncedSearch) count++;
    if (roles.length > 0) count++;
    if (sites.length > 0) count++;
    if (stages.length > 0) count++;
    if (jobPostingId) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [debouncedSearch, roles, sites, stages, jobPostingId, dateFrom, dateTo]);

  return {
    searchInput,
    debouncedSearch,
    roles,
    sites,
    stages,
    jobPostingId,
    dateFrom,
    dateTo,
    page,
    sortBy,
    sortOrder,
    activeFilterCount,
    setSearchInput,
    setRoles,
    setSites,
    setStages,
    setJobPostingId,
    setDateFrom,
    setDateTo,
    setPage,
    setSortBy,
    setSortOrder,
    handleSort,
    clearAll,
    viewMode,
    setViewMode,
  };
}
